import { formatCurrency } from "@talladega/helpers";
import { useNumberMask } from "@common/hooks/useMask";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { Dispatch } from "react";
import { TranslationKeys } from "src/@types/i18next";
import {
  BillOfSaleFormState,
  BillOfSaleStepAction,
} from "../hooks/useBillOfSaleFormStepsReducer";

type Props = {
  billOfSaleReducer: [BillOfSaleFormState, Dispatch<BillOfSaleStepAction>];
};

const BillOfSaleDetailsInfo = ({ billOfSaleReducer }: Props) => {
  const { t } = useTranslation(["billOfSalePage", "common"]);
  const router = useRouter();
  const locale = router?.locale || "en";
  const [
    { isVinDecodeComplete, isVehicleDetailsStepComplete, formInformation },
  ] = billOfSaleReducer;

  const { vehicleDetails, sellerInformation, buyerInformation } =
    formInformation;

  const hasSellerInformation =
    !!sellerInformation.firstName ||
    !!sellerInformation.lastName ||
    !!sellerInformation.streetAddress ||
    !!sellerInformation.city ||
    !!sellerInformation.province ||
    !!sellerInformation.postalCode;

  const hasBuyerInformation =
    !!buyerInformation.firstName ||
    !!buyerInformation.lastName ||
    !!buyerInformation.streetAddress ||
    !!buyerInformation.city ||
    !!buyerInformation.province ||
    !!buyerInformation.postalCode;

  const { unMaskPipe: unMaskNumberPipe } = useNumberMask();

  const buyerProvince = buyerInformation.province?.name
    ? t(buyerInformation.province?.name as TranslationKeys["common"])
    : "";
  const sellerProvince = sellerInformation.province?.name
    ? t(sellerInformation.province?.name as TranslationKeys["common"])
    : "";

  return (
    <div className="hidden lg:block items-center m-12">
      <div className="p-4">
        <div className="body-1 mb-6 font-semibold">{t("common:summary")}</div>
        {isVinDecodeComplete && (
          <div>
            <div className="body-1 mb-1 break-words font-semibold">{`${vehicleDetails.year} ${vehicleDetails.make} ${vehicleDetails.model}`}</div>

            {isVehicleDetailsStepComplete && (
              <div className="caption-1 mb-1 break-words font-semibold">{`${
                vehicleDetails.trim
              } ${vehicleDetails.trim === "" ? "" : "|"} ${t(
                "common:count_km",
                {
                  kmCount: vehicleDetails.odometer,
                }
              )}`}</div>
            )}

            <div className="flex justify-between body-3 mt-6 mb-2">
              <span>{t("common:vehicle_vin")}</span>
              <span>{vehicleDetails.vin}</span>
            </div>

            {isVehicleDetailsStepComplete && (
              <div className="flex justify-between body-3 mb-2">
                <span>{t("common:purchase_price")}</span>
                {vehicleDetails.sellingPrice &&
                  unMaskNumberPipe(vehicleDetails.sellingPrice.toString()) !==
                    0 && (
                    <span>
                      {formatCurrency(
                        unMaskNumberPipe(
                          vehicleDetails.sellingPrice.toString()
                        ),
                        locale,
                        { showCents: "never" }
                      )}
                    </span>
                  )}
              </div>
            )}

            <div className="mt-2 mb-4 border-t-2 border-t-gray-300" />
          </div>
        )}
        {hasSellerInformation && (
          <>
            <div className="flex justify-between body-3 mb-2 gap-x-2">
              <span>{t("billOfSalePage:seller_name")}</span>
              <span className="w-[72%] text-right break-words">
                {sellerInformation.firstName && sellerInformation.firstName}{" "}
                {sellerInformation.lastName && sellerInformation.lastName}
              </span>
            </div>
            <div className="flex justify-between body-3 mb-2 gap-x-2">
              <span>{t("billOfSalePage:seller_address")}</span>
              <div className="w-[72%] text-right break-words">
                {sellerInformation.streetAddress && (
                  <div>{sellerInformation.streetAddress}</div>
                )}
                {sellerInformation.city && sellerProvince && (
                  <div>{`${sellerInformation.city}, ${sellerProvince}`}</div>
                )}
                <div>{sellerInformation.postalCode}</div>
              </div>
            </div>
            <div className="mt-2 mb-4 border-t-2 border-t-gray-300" />
          </>
        )}

        {hasBuyerInformation && (
          <div>
            <div className="flex justify-between body-3 mb-2">
              <span>{t("billOfSalePage:buyer_name")}</span>

              <span className="w-[72%] text-right break-words">
                {buyerInformation.firstName && buyerInformation.firstName}{" "}
                {buyerInformation.lastName && buyerInformation.lastName}
              </span>
            </div>
            <div className="flex justify-between body-3 mb-2">
              <span>{t("billOfSalePage:buyer_address")}</span>
              <div className="w-[72%] text-right break-words">
                {buyerInformation.streetAddress && (
                  <div>{buyerInformation.streetAddress}</div>
                )}
                {buyerInformation.city && buyerProvince && (
                  <div>{`${buyerInformation.city}, ${buyerProvince}`}</div>
                )}
                {buyerInformation.postalCode && (
                  <div>{buyerInformation.postalCode}</div>
                )}
              </div>
            </div>
            <div className="mt-2 mb-4 border-t-2 border-t-gray-300" />
          </div>
        )}
      </div>
    </div>
  );
};

export default BillOfSaleDetailsInfo;
